import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { footageService } from '../services/FootageService';
import { incidentLogService } from '../services/IncidentLogService';
import { reviewService } from '../services/ReviewService';
import { RBTDashboard } from '../components/RBTDashboard';
import { SupervisorDashboard } from '../components/SupervisorDashboard';
import {
  Video,
  FileText,
  Eye,
  Upload,
  HardDrive,
} from 'lucide-react';

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { company, loading: companyLoading } = useCompany();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalFootage: 0,
    totalIncidents: 0,
    totalReviews: 0,
    storageUsed: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  const loadStats = async () => {
    if (!user || !company) {
      setStatsLoading(false);
      return;
    }

    try {
      const footage = await footageService.getFootageByCompany(
        company.id,
        1000
      );
      const incidents = await incidentLogService.getIncidentLogsByCompany(
        company.id,
        1000
      );
      const reviews = await reviewService.getReviewsByCompany(company.id);

      const storageUsed = footage.reduce(
        (total, file) => total + file.fileSize,
        0
      );

      setStats({
        totalFootage: footage.length,
        totalIncidents: incidents.length,
        totalReviews: reviews.length,
        storageUsed: storageUsed / (1024 * 1024 * 1024), // Convert to GB
      });
    } catch (error) {
      console.error('Dashboard: Error loading stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    // Wait for company to finish loading before attempting to load stats
    if (!companyLoading) {
      loadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, company, companyLoading]);

  const StatCard: React.FC<{
    icon: React.ReactNode;
    title: string;
    value: string | number;
    color: string;
  }> = ({ icon, title, value, color }) => (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)' }}>
        <div
          style={{
            padding: 'var(--spacing-3)',
            borderRadius: 'var(--radius-lg)',
            backgroundColor: color,
            color: 'var(--color-white)',
          }}
        >
          {icon}
        </div>
        <div>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>{title}</p>
          <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-gray-800)' }}>
            {value}
          </p>
        </div>
      </div>
    </div>
  );

  // Show loading spinner while company is loading OR stats are loading
  const isLoading = companyLoading || statsLoading;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-block',
              width: '48px',
              height: '48px',
              border: '4px solid var(--color-gray-200)',
              borderTop: '4px solid var(--color-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ marginTop: 'var(--spacing-4)', color: 'var(--color-gray-600)' }}>
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  // Render simplified RBT dashboard for RBT users
  if (user?.role === 'rbt') {
    return (
      <div className="container" style={{ paddingTop: 'var(--spacing-8)', paddingBottom: 'var(--spacing-8)' }}>
        <RBTDashboard />
      </div>
    );
  }

  // Render supervisor dashboard for supervisor users
  if (user?.role === 'supervisor') {
    return (
      <div className="container" style={{ paddingTop: 'var(--spacing-8)', paddingBottom: 'var(--spacing-8)' }}>
        <SupervisorDashboard />
      </div>
    );
  }

  // Render full dashboard for admin and viewer roles
  return (
    <div className="container" style={{ paddingTop: 'var(--spacing-8)', paddingBottom: 'var(--spacing-8)' }}>
        {/* Header */}
        <div style={{ marginBottom: 'var(--spacing-8)' }}>
          <h1 style={{ marginBottom: 'var(--spacing-2)' }}>
            Welcome back, {user?.name}!
          </h1>
          <p style={{ color: 'var(--color-gray-600)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            {user?.email}
            <span className={`role-badge role-${user?.role}`}>
              {user?.role?.toUpperCase()}
            </span>
          </p>
        </div>

        {/* Quick Actions */}
        <div style={{ marginBottom: 'var(--spacing-8)', display: 'flex', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/upload')}
            className="btn btn-primary btn-lg"
          >
            <Upload size={20} style={{ marginRight: 'var(--spacing-2)' }} />
            Upload Footage
          </button>
          <button
            onClick={() => navigate('/incidents/new')}
            className="btn btn-secondary btn-lg"
          >
            <FileText size={20} style={{ marginRight: 'var(--spacing-2)' }} />
            Create Incident Log
          </button>
          <button
            onClick={() => navigate('/footage')}
            className="btn btn-ghost btn-lg"
          >
            <Video size={20} style={{ marginRight: 'var(--spacing-2)' }} />
            View Footage
          </button>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-4" style={{ marginBottom: 'var(--spacing-8)' }}>
          <StatCard
            icon={<Video size={24} />}
            title="Total Footage"
            value={stats.totalFootage}
            color="var(--color-primary)"
          />
          <StatCard
            icon={<FileText size={24} />}
            title="Incident Logs"
            value={stats.totalIncidents}
            color="var(--color-secondary)"
          />
          <StatCard
            icon={<Eye size={24} />}
            title="Reviews"
            value={stats.totalReviews}
            color="var(--color-success)"
          />
          <StatCard
            icon={<HardDrive size={24} />}
            title="Storage Used"
            value={`${stats.storageUsed.toFixed(2)} GB`}
            color="var(--color-gray-600)"
          />
        </div>

        {/* Storage Progress */}
        <div className="card" style={{ marginBottom: 'var(--spacing-8)' }}>
          <div className="card-header">
            <h2 className="card-title">Storage Usage</h2>
          </div>
          <div style={{ marginBottom: 'var(--spacing-2)', display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)' }}>
            <span style={{ color: 'var(--color-gray-600)' }}>
              {stats.storageUsed.toFixed(2)} GB used
            </span>
            <span style={{ color: 'var(--color-gray-600)' }}>
              {company?.subscription?.storageLimit || 0} GB total
            </span>
          </div>
          <div style={{ width: '100%', backgroundColor: 'var(--color-gray-200)', borderRadius: 'var(--radius-lg)', height: '12px' }}>
            <div
              style={{
                backgroundColor: 'var(--color-secondary)',
                height: '12px',
                borderRadius: 'var(--radius-lg)',
                transition: 'width 0.3s ease',
                width: `${Math.min(
                  (stats.storageUsed / (company?.subscription?.storageLimit || 1)) * 100,
                  100
                )}%`,
              }}
            />
          </div>
          <p style={{ marginTop: 'var(--spacing-2)', fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
            Plan: {company?.subscription?.plan?.toUpperCase() || 'N/A'}
          </p>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-2">
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Quick Links</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
              <button
                onClick={() => navigate('/footage')}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: 'var(--spacing-3)',
                  borderRadius: 'var(--radius-lg)',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-3)',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-gray-100)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <Video style={{ color: 'var(--color-primary)' }} size={20} />
                <span>Browse All Footage</span>
              </button>
              <button
                onClick={() => navigate('/incidents')}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: 'var(--spacing-3)',
                  borderRadius: 'var(--radius-lg)',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-3)',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-gray-100)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <FileText style={{ color: 'var(--color-primary)' }} size={20} />
                <span>View Incident Logs</span>
              </button>
              <button
                onClick={() => navigate('/reviews')}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: 'var(--spacing-3)',
                  borderRadius: 'var(--radius-lg)',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-3)',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-gray-100)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <Eye style={{ color: 'var(--color-primary)' }} size={20} />
                <span>Review History</span>
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2 className="card-title">System Information</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)', fontSize: 'var(--text-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-gray-600)' }}>Company Name:</span>
                <span style={{ fontWeight: 'var(--font-medium)' }}>{company?.name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-gray-600)' }}>Your Role:</span>
                <span style={{ fontWeight: 'var(--font-medium)' }}>{user?.role?.toUpperCase() || 'N/A'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-gray-600)' }}>User Limit:</span>
                <span style={{ fontWeight: 'var(--font-medium)' }}>
                  {company?.subscription?.userLimit || 0} users
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-gray-600)' }}>Folder Structure:</span>
                <span style={{ fontWeight: 'var(--font-medium)' }}>
                  {company?.settings?.folderStructure?.toUpperCase() || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
  );
};
